import { createPortal } from 'react-dom'
import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react'
import type { BusinessFlowLaneStep } from '@/types/knowledge_graph'

export function FlowStepBubble ({ step, className }: { step: BusinessFlowLaneStep, className: string }): ReactElement {
  const [hovered, setHovered] = useState(false)
  const [pinned, setPinned] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [placement, setPlacement] = useState<'below' | 'above'>('below')
  const hasDescription = step.description != null && step.description !== ''
  const open = hasDescription && (hovered || pinned)

  useEffect(() => {
    if (open) setRect(anchorRef.current?.getBoundingClientRect() ?? null)
  }, [open])

  useLayoutEffect(() => {
    if (!open || (rect == null)) return
    const popoverHeight = popoverRef.current?.offsetHeight ?? 0
    const spaceBelow = window.innerHeight - rect.bottom
    setPlacement(spaceBelow < popoverHeight + 12 ? 'above' : 'below')
  }, [open, rect])

  useEffect(() => {
    if (!pinned) return
    function handleClickOutside (e: MouseEvent): void {
      if (anchorRef.current?.contains(e.target as Node) !== true) setPinned(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [pinned])

  return (
    <div
      ref={anchorRef}
      className={className}
      style={{ cursor: hasDescription ? 'pointer' : undefined }}
      onMouseEnter={() => { if (hasDescription) setHovered(true) }}
      onMouseLeave={() => setHovered(false)}
      onClick={() => { if (hasDescription) setPinned((p) => !p) }}
    >
      {step.event}
      {open && (rect != null) && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            ...(placement === 'below'
              ? { top: rect.bottom + 6 }
              : { top: 'auto', bottom: window.innerHeight - rect.top + 6 }
            ),
            left: Math.min(rect.left, window.innerWidth - 300),
            width: 280,
            zIndex: 1000,
            background: 'var(--bp-surface)',
            border: '1.5px solid var(--bp-border)',
            borderRadius: 8,
            padding: '10px 12px',
            boxShadow: '0 12px 32px rgba(0,0,0,.18)'
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--bp-ink2)', lineHeight: 1.5 }}>
            {step.description}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
